import { useCallback } from 'react';
import useHttp from './useHttp';
import { useAuth } from '../context/authContext';

// Encapsulates all e-mail marketing API calls: contact lists, flows (builder
// canvas + activation) and the dashboard data. The mails themselves are sent
// by GetResponse — the flow only orchestrates vouchers, waits, conditions and
// the GetResponse data exchange.
export default function useEmailMarketing() {
  const { isLoading, error, sendRequest } = useHttp();
  const { getAuthHeaders } = useAuth();

  /* --------------------------- contact lists --------------------------- */

  const getLists = useCallback(
    async () =>
      sendRequest({
        url: '/api/v1/marketingContactList',
        method: 'GET',
        headers: getAuthHeaders(),
      }),
    [getAuthHeaders, sendRequest]
  );

  const createList = useCallback(
    async ({ name, description }) =>
      sendRequest({
        url: '/api/v1/marketingContactList',
        method: 'POST',
        headers: getAuthHeaders(),
        body: { name, description },
      }),
    [getAuthHeaders, sendRequest]
  );

  const getListMembers = useCallback(
    async (listId, { page = 1, limit = 50 } = {}) =>
      sendRequest({
        url: `/api/v1/marketingContactList/${listId}/members`,
        method: 'GET',
        headers: getAuthHeaders(),
        params: { page, limit },
      }),
    [getAuthHeaders, sendRequest]
  );

  const addListMembers = useCallback(
    async (listId, emails) =>
      sendRequest({
        url: `/api/v1/marketingContactList/${listId}/members`,
        method: 'POST',
        headers: getAuthHeaders(),
        body: { emails },
      }),
    [getAuthHeaders, sendRequest]
  );

  const removeListMember = useCallback(
    async (listId, membershipId) =>
      sendRequest({
        url: `/api/v1/marketingContactList/${listId}/members/${membershipId}`,
        method: 'DELETE',
        headers: getAuthHeaders(),
      }),
    [getAuthHeaders, sendRequest]
  );

  const syncBirthdayAudience = useCallback(
    async listId =>
      sendRequest({
        url: `/api/v1/marketingContactList/${listId}/syncBirthdayAudience`,
        method: 'POST',
        headers: getAuthHeaders(),
      }),
    [getAuthHeaders, sendRequest]
  );

  /* ------------------------------- flows ------------------------------- */

  const getFlows = useCallback(
    async () =>
      sendRequest({
        url: '/api/v1/marketingFlow',
        method: 'GET',
        headers: getAuthHeaders(),
      }),
    [getAuthHeaders, sendRequest]
  );

  const getFlowById = useCallback(
    async flowId =>
      sendRequest({
        url: `/api/v1/marketingFlow/${flowId}`,
        method: 'GET',
        headers: getAuthHeaders(),
      }),
    [getAuthHeaders, sendRequest]
  );

  // Creates a new, empty flow (generic — no birthday hard-coding).
  const createFlow = useCallback(
    async ({ name, blocks, connections } = {}) =>
      sendRequest({
        url: '/api/v1/marketingFlow',
        method: 'POST',
        headers: getAuthHeaders(),
        body: { name, blocks, connections },
      }),
    [getAuthHeaders, sendRequest]
  );

  // The "Aktualisieren" button: saves blocks + connections at any time.
  const updateFlow = useCallback(
    async (flowId, payload) =>
      sendRequest({
        url: `/api/v1/marketingFlow/${flowId}`,
        method: 'PUT',
        headers: getAuthHeaders(),
        body: payload,
      }),
    [getAuthHeaders, sendRequest]
  );

  const validateFlow = useCallback(
    async flowId =>
      sendRequest({
        url: `/api/v1/marketingFlow/${flowId}/validate`,
        method: 'GET',
        headers: getAuthHeaders(),
      }),
    [getAuthHeaders, sendRequest]
  );

  const activateFlow = useCallback(
    async flowId =>
      sendRequest({
        url: `/api/v1/marketingFlow/${flowId}/activate`,
        method: 'POST',
        headers: getAuthHeaders(),
      }),
    [getAuthHeaders, sendRequest]
  );

  const pauseFlow = useCallback(
    async flowId =>
      sendRequest({
        url: `/api/v1/marketingFlow/${flowId}/pause`,
        method: 'POST',
        headers: getAuthHeaders(),
      }),
    [getAuthHeaders, sendRequest]
  );

  // Staging-only immediate test run: plays the whole flow through for the given
  // addresses right now, with every wait collapsed. Not a dry run — vouchers are
  // issued and the mails GetResponse sends on the flow's tags really go out.
  const testRunFlow = useCallback(
    async (flowId, emails) =>
      sendRequest({
        url: `/api/v1/marketingFlow/${flowId}/test-run`,
        method: 'POST',
        headers: getAuthHeaders(),
        body: { emails },
      }),
    [getAuthHeaders, sendRequest]
  );

  const enrollContacts = useCallback(
    async (flowId, emails) =>
      sendRequest({
        url: `/api/v1/marketingFlow/${flowId}/enroll`,
        method: 'POST',
        headers: getAuthHeaders(),
        body: { emails },
      }),
    [getAuthHeaders, sendRequest]
  );

  const getEnrollments = useCallback(
    async (flowId, { status, search, page = 1, limit = 50 } = {}) =>
      sendRequest({
        url: `/api/v1/marketingFlow/${flowId}/enrollments`,
        method: 'GET',
        headers: getAuthHeaders(),
        params: { status, search, page, limit },
      }),
    [getAuthHeaders, sendRequest]
  );

  const cancelEnrollment = useCallback(
    async (flowId, enrollmentId) =>
      sendRequest({
        url: `/api/v1/marketingFlow/${flowId}/enrollments/${enrollmentId}/cancel`,
        method: 'POST',
        headers: getAuthHeaders(),
        body: {},
      }),
    [getAuthHeaders, sendRequest]
  );

  /* ----------------------------- funnel KPIs ---------------------------- */

  const getFunnelKpis = useCallback(
    async () =>
      sendRequest({
        url: '/api/v1/marketingFlow/funnel-kpis',
        method: 'GET',
        headers: getAuthHeaders(),
      }),
    [getAuthHeaders, sendRequest]
  );

  /* ----------------------------- dashboard ----------------------------- */

  const getDashboard = useCallback(
    async ({ from, to, flowId } = {}) =>
      sendRequest({
        url: '/api/v1/marketingFlow/dashboard',
        method: 'GET',
        headers: getAuthHeaders(),
        params: { from, to, flowId },
      }),
    [getAuthHeaders, sendRequest]
  );

  return {
    isLoading,
    error,
    getLists,
    createList,
    getListMembers,
    addListMembers,
    removeListMember,
    syncBirthdayAudience,
    getFlows,
    getFlowById,
    createFlow,
    updateFlow,
    validateFlow,
    activateFlow,
    pauseFlow,
    testRunFlow,
    getDashboard,
    getFunnelKpis,
    enrollContacts,
    getEnrollments,
    cancelEnrollment,
  };
}
